import { useEffect, useId, useRef } from "react";

const STRIPE_API_KEYS_URL = "https://dashboard.stripe.com/apikeys";
const STRIPE_PAYMENT_METHODS_URL = "https://dashboard.stripe.com/settings/payment_methods";
const STRIPE_PAYMENT_METHOD_DOMAINS_URL =
  "https://dashboard.stripe.com/settings/payment_method_domains?enabled=true";
const PAY_DOMAIN = "play.teamtable.co";

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent hover:underline break-all"
    >
      {children}
    </a>
  );
}

export function StripeSetupHelpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="presentation"
    >
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-t-xl sm:rounded-xl border border-border bg-surface-raised shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4 shrink-0">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-text-primary">
              Set up Stripe for this fleet
            </h2>
            <p className="mt-1 text-sm text-text-muted">
              Each fleet uses its own Stripe account. Open the links below while signed into that
              account — Stripe routes you to the right place from your session, so there is no
              account ID to paste.
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md px-2 py-1 text-text-muted hover:bg-surface hover:text-text-primary transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <ol className="overflow-y-auto px-5 py-4 space-y-6 text-sm text-text-secondary list-decimal list-outside ml-5">
          <li>
            <p className="font-medium text-text-primary">Create and activate a Stripe account</p>
            <p className="mt-1">
              Sign up at{" "}
              <ExternalLink href="https://dashboard.stripe.com/register">
                dashboard.stripe.com
              </ExternalLink>{" "}
              (or use an existing account). Finish business details and bank payouts so live charges
              can settle.
            </p>
          </li>

          <li>
            <p className="font-medium text-text-primary">Copy your API keys</p>
            <p className="mt-1">
              From{" "}
              <ExternalLink href={STRIPE_API_KEYS_URL}>
                dashboard.stripe.com/apikeys
              </ExternalLink>
              , copy the <span className="font-mono text-text-primary">Publishable key</span> and{" "}
              <span className="font-mono text-text-primary">Secret key</span>. Paste them into the
              form on this page.
            </p>
          </li>

          <li>
            <p className="font-medium text-text-primary">Enable payment methods</p>
            <p className="mt-1">
              Open{" "}
              <ExternalLink href={STRIPE_PAYMENT_METHODS_URL}>
                Payment methods
              </ExternalLink>
              . We recommend enabling only <strong className="text-text-primary">Cards</strong>,{" "}
              <strong className="text-text-primary">Apple Pay</strong>, and{" "}
              <strong className="text-text-primary">Google Pay</strong>. Click a row to open the side
              panel, then use Enable / Disable.
            </p>
            <figure className="mt-3">
              <img
                src="/stripe-help/payment-methods.png"
                alt="Stripe Payment methods page with Cards, Apple Pay, and Google Pay enabled; Cards detail panel open on the right"
                className="w-full rounded-lg border border-border"
              />
              <figcaption className="mt-2 text-xs text-text-muted">
                Settings → Payments → Payment methods — enable Cards, Apple Pay, and Google Pay.
              </figcaption>
            </figure>
          </li>

          <li>
            <p className="font-medium text-text-primary">Register the Apple Pay domain</p>
            <p className="mt-1">
              Open{" "}
              <ExternalLink href={STRIPE_PAYMENT_METHOD_DOMAINS_URL}>
                Payment method domains
              </ExternalLink>
              , add a domain, and enter{" "}
              <span className="font-mono text-text-primary">{PAY_DOMAIN}</span>. You can ignore the
              Domain ID — TeamTable does not need it.
            </p>
            <figure className="mt-3">
              <img
                src="/stripe-help/payment-method-domains.png"
                alt={`Stripe Payment method domains page with ${PAY_DOMAIN} listed as Enabled`}
                className="w-full rounded-lg border border-border"
              />
              <figcaption className="mt-2 text-xs text-text-muted">
                Add <span className="font-mono">{PAY_DOMAIN}</span> so Apple Pay works on the pay
                page.
              </figcaption>
            </figure>
          </li>

          <li>
            <p className="font-medium text-text-primary">Smoke-test on a Team Table</p>
            <p className="mt-1">
              On a device in this fleet, run a Card, Apple Pay, and Google Pay payment through the
              pay flow. Confirm each payment appears in this Stripe account&apos;s Dashboard
              (Payments). Manual capture means you may see an authorization first, then a capture
              once the table unlocks.
            </p>
          </li>
        </ol>

        <div className="border-t border-border px-5 py-4 shrink-0 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export function StripeSetupHelpButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-md bg-accent/15 border border-accent/40 px-3 py-1.5 text-sm font-medium text-accent hover:bg-accent/25 hover:border-accent transition-colors"
      aria-haspopup="dialog"
    >
      <span
        className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-white text-xs font-bold leading-none"
        aria-hidden
      >
        ?
      </span>
      How to set up Stripe
    </button>
  );
}
